import * as React from "react";

import { useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ScheduleInputField } from "@/components/organisms/ScheduleInputField";
import { useListingData } from "~/context/ListingPageDataContext";

import { useBookListingContext } from "../BookListingContext";
import { useListingAvailabilityLoaders } from "../hooks/use-listing-availability-loaders";
import { getMinDuration } from "../listing-availability.utils";

export const BookingScheduleInput = () => {
  const intl = useIntl();
  const { setAlertConfig, bookListingForm, enforceMinimumHours } =
    useBookListingContext();

  const [initialRender, setInitialRender] = React.useState(true);

  const { data: listingData } = useListingData();
  const { listingId, fullService } = listingData || {};
  const isFullServiceVenue = !!fullService?.active;

  const minDuration = getMinDuration(listingData ?? {});

  const handleMonthAvailabilityLoaded = (bucket: string) => {
    window.analyticsIntegrations.calendarSync(
      listingId,
      isFullServiceVenue,
      bucket,
    );
  };

  const { getCalendarModifiers, getTimeOptions } =
    useListingAvailabilityLoaders({
      enforceMinimumHours,
      listingData,
      onMonthAvailabilityLoaded: handleMonthAvailabilityLoaded,
    });

  const isAutoCorrected = React.useRef(false);
  const onAutoCorrect = (
    modificationType?: "minDuration" | "exactDuration",
  ) => {
    isAutoCorrected.current = true;
    if (modificationType === "minDuration") {
      setAlertConfig({
        isVisible: true,
        message: intl.formatMessage(
          {
            defaultMessage:
              "Your estimate was updated to meet venue's {minDuration} hr minimum.",
          },
          { minDuration },
        ),
      });
    }
  };

  const watchSchedule = useWatch({
    control: bookListingForm.control,
    name: "schedule",
  });
  // effect to reset alert visibility when schedule changes
  React.useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
      return;
    }

    if (!isAutoCorrected.current) {
      setAlertConfig((prev) => ({
        ...prev,
        isVisible: false,
      }));
    } else {
      isAutoCorrected.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchSchedule]);

  return (
    <ScheduleInputField
      testId="book-listing-schedule-input-field"
      dayPickerDataTestingId="dayPickerButton"
      hideExtendDayBtn={isFullServiceVenue}
      getCalendarModifiers={getCalendarModifiers}
      getTimeOptions={getTimeOptions}
      fullWidth
      locale={intl.locale}
      datePickerProps={{
        displayCalendarIcon: false,
        label: <FormattedMessage defaultMessage="Date and time" />,
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "start date",
        }),
        calendarProps: {
          mode: "single",
          locale: intl.locale,
          numberOfMonths: 1,
        },
      }}
      timeSelectProps={{
        autoCorrectConfig: {
          minDuration,
          onAutoCorrect,
        },
        startTimeProps: {
          placeholder: intl.formatMessage({
            defaultMessage: "Start time",
          }),
        },
        endTimeProps: {
          placeholder: intl.formatMessage({
            defaultMessage: "End time",
          }),
        },
        displayTimeIcon: false,
      }}
    />
  );
};
